import React, { useCallback, useContext, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@material-ui/core';
import { v4 as uuid } from 'uuid';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { TeamModuleContext } from '@vyce/core/src/contexts';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

import { EmployeesForTransferList } from './EmployeesForTransferList';
import { MAIN_CONTAINER_ID } from '../../../constants';
import { NotificationContext } from '../../../contexts/notificationContext';

interface Props {
  open: boolean;
  setOpen: Function;
  setSignalForRerender: (arg0: string) => void;
}

export const TransferEmployeesDialog: React.FC<Props> = ({ open, setOpen, setSignalForRerender }) => {
  const { isDesktop, isMobile } = useContext(DeviceContext);
  const { selectionModel, teamId, selectedCompanyId, addTeamMembersRequest } = useContext(TeamModuleContext);
  const { handleServerError } = useContext(NotificationContext);
  const [loading, setLoading] = useState<boolean>(false);

  const handleTransfer = useCallback(async () => {
    if (!selectedCompanyId || !teamId) {
      return;
    }
    try {
      setLoading(true);
      const enrichedModel = selectionModel.map(item => ({
        user_id: item,
      }));
      const requestData = { members: enrichedModel };
      await addTeamMembersRequest(selectedCompanyId, teamId, requestData);
      setSignalForRerender(uuid());
      setOpen(false);
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId, teamId, selectionModel, addTeamMembersRequest, setSignalForRerender]);

  return (
    <Dialog
      fullScreen={isMobile}
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      maxWidth="lg"
      onClose={() => setOpen(false)}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">Add existing team members</DialogTitle>

      <DialogContent style={{ width: isDesktop ? '1000px' : '100%' }}>
        <DialogContentText>Select existing team members to add to this team.</DialogContentText>
        {open && <EmployeesForTransferList />}
      </DialogContent>

      <DialogActions>
        <Button size="large" disabled={loading} variant="outlined" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          style={{ width: 90 }}
          size="large"
          color="primary"
          cy-test-id="add-existing-user-to-team-button"
          onClick={handleTransfer}
          disabled={!selectionModel.length || loading}
          variant="contained"
        >
          <ButtonTitleWithLoading title="Add" loaderVariant="paper" loading={loading} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
